import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Edit, Trash2, Plus, FileText, Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { documentsAPI } from '@/utils/api'
import './DocumentList.css'

// German translations
const translations = {
  de: {
    loading: 'Dokumente werden geladen...',
    errorLoading: 'Fehler beim Laden der Dokumente',
    retry: 'Erneut versuchen',
    title: 'Rechtsdokumente',
    documentsCount: (count) => `${count} ${count === 1 ? 'Dokument' : 'Dokumente'}`,
    newDocument: 'Neues Dokument',
    noDocuments: 'Noch keine Dokumente',
    noDocumentsDesc: 'Erstellen Sie Ihr erstes Rechtsdokument',
    createDocument: 'Dokument erstellen',
    updated: 'Aktualisiert',
    created: 'Erstellt',
    editDocument: 'Dokument bearbeiten',
    deleteDocument: 'Dokument löschen',
    deleteTitle: 'Dokument löschen',
    deleteConfirm: (title) => `Möchten Sie "${title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
    cancel: 'Abbrechen',
    delete: 'Löschen',
    deleting: 'Wird gelöscht...',
    deleteSuccess: 'Dokument wurde gelöscht',
    deleteError: 'Fehler beim Löschen des Dokuments',
  },
  en: {
    loading: 'Loading documents...',
    errorLoading: 'Error loading documents',
    retry: 'Retry',
    title: 'Legal Documents',
    documentsCount: (count) => `${count} ${count === 1 ? 'document' : 'documents'}`,
    newDocument: 'New Document',
    noDocuments: 'No documents yet',
    noDocumentsDesc: 'Create your first legal document to get started',
    createDocument: 'Create Document',
    updated: 'Updated',
    created: 'Created',
    editDocument: 'Edit document',
    deleteDocument: 'Delete document',
    deleteTitle: 'Delete Document',
    deleteConfirm: (title) => `Are you sure you want to delete "${title}"? This action cannot be undone.`,
    cancel: 'Cancel',
    delete: 'Delete',
    deleting: 'Deleting...',
    deleteSuccess: 'Document deleted successfully',
    deleteError: 'Failed to delete document',
  },
}

export default function DocumentList({ onEditDocument, onNewDocument, caseId = null, locale = 'de' }) {
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const t = translations[locale] || translations.de

  // Fetch documents on component mount or when caseId changes
  useEffect(() => {
    fetchDocuments()
  }, [caseId])

  const fetchDocuments = async () => {
    try {
      setLoading(true)
      setError(null)
      // Use centralized API - note: getDocuments doesn't support caseId filter yet
      // TODO: Add caseId filter support to documentsAPI.getDocuments if needed
      const data = await documentsAPI.getDocuments()
      if (data.status === 'success') {
        // Filter by caseId on client side if provided
        const filteredDocs = caseId
          ? data.data.filter(doc => doc.case === caseId)
          : data.data
        setDocuments(filteredDocs)
      } else {
        throw new Error('Failed to fetch documents')
      }
    } catch (err) {
      console.error('Error fetching documents:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClick = (document) => {
    setDocumentToDelete(document)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return

    try {
      setDeleting(true)
      await documentsAPI.deleteLegalDocument(documentToDelete.id)

      // Remove the document from the list
      setDocuments(documents.filter(doc => doc.id !== documentToDelete.id))
      setDeleteModalOpen(false)
      setDocumentToDelete(null)
      toast.success(t.deleteSuccess)
    } catch (err) {
      console.error('Error deleting document:', err)
      toast.error(t.deleteError)
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(locale === 'de' ? 'de-DE' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <p className="text-slate-500">{t.loading}</p>
      </div>
    )
  }

  if (error) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center gap-4">
          <p className="text-red-500 font-medium">{t.errorLoading}</p>
          <p className="text-sm text-slate-500">{error}</p>
          <Button onClick={fetchDocuments}>{t.retry}</Button>
        </div>
      </Card>
    )
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header with New Document button */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{t.title}</h2>
            <p className="text-sm text-slate-500">
              {t.documentsCount(documents.length)}
            </p>
          </div>
          <Button onClick={onNewDocument} className="gap-1">
            <Plus className="h-4 w-4" />
            {t.newDocument}
          </Button>
        </div>

        {/* Document List */}
        {documents.length === 0 ? (
          <Card className="p-8">
            <div className="flex flex-col items-center gap-4 py-8">
              <FileText className="h-12 w-12 text-slate-300" strokeWidth={1.5} />
              <div className="text-center">
                <p className="font-medium text-lg">{t.noDocuments}</p>
                <p className="text-sm text-slate-500">
                  {t.noDocumentsDesc}
                </p>
              </div>
              <Button onClick={onNewDocument} variant="outline" className="gap-1">
                <Plus className="h-4 w-4" />
                {t.createDocument}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {documents.map((document) => (
              <Card
                key={document.id}
                className="p-4 cursor-pointer hover:shadow-md transition-shadow document-card"
                onClick={() => onEditDocument(document)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">
                      {document.title}
                    </h3>
                    {document.preview && (
                      <p className="text-sm text-slate-500 line-clamp-2 mb-2">
                        {document.preview}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Badge variant="info" className="text-xs">
                        {t.updated}: {formatDate(document.updated_at)}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {t.created}: {formatDate(document.created_at)}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1 document-card-actions">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation()
                        onEditDocument(document)
                      }}
                      title={t.editDocument}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteClick(document)
                      }}
                      title={t.deleteDocument}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.deleteConfirm(documentToDelete?.title)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? t.deleting : t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
